import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import MoreInfo from '../MoreInfo';
import Card from '../Card';
import RegistrarRow from './RegistrarRow';
import { mediaMobile } from '../../utils/getResponsiveTag';
import { get } from '../../utils/request';
import ErrorMessage from './ErrorMessage';

// QUERY_SELECTOR will be replace by the selected domain
// in order to create a custom checkout URL
const QUERY_SELECTOR = '{}';
const API_URL = 'home/optinregistrars';
const ERROR_MSG = 'Something went wrong, please try again later.';
const LOADING_MSG = 'Loading the registrars...';
const INITIAL_DISPLAYED_REGISTRAR_SIZE = 8;

function Step3({ selectedDomain, helperStepThree, groupInTiers, isJumpstarter }) {
  if (!selectedDomain) return null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [allRegistrars, setAllRegistrars] = useState([]);
  const [cardRegistrars, setCardRegistrars] = useState([]);
  const [listRegistrars, setListRegistrars] = useState([]);
  const [showAllRegistrars, setShowAllRegistrars] = useState(false);
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    setError(false);
    setLoading(true);

    async function fetchData() {
      try {
        const response = await get(API_URL, '', {
          headers: { Accept: 'application/json' },
        });
        const registrars = groupInTiers ? response.data.filter(({ CheckoutUrl }) => !!CheckoutUrl) : response.data;
        setAllRegistrars(registrars);
        if (groupInTiers) {
          // JumpStarter displays results differently, with gold & silver as cards, and showAll will display the rest as a list below.
          setCardRegistrars([
            ...registrars.filter((registrar) => registrar.Tier === 'Gold'),
            ...registrars.filter((registrar) => registrar.Tier === 'Silver')
          ]);
        } else {
          // Whereas INZ shows all as cards, and showAll will just show all cards
          setCardRegistrars(registrars.slice(0, INITIAL_DISPLAYED_REGISTRAR_SIZE));
        }
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
        console.warn(error);
      }
    }
    fetchData();
  }, [filters]);

  const showAll = () => {
    setShowAllRegistrars(true);
    if (groupInTiers) {
      setListRegistrars(allRegistrars.filter((registrar) => registrar.Tier === 'Bronze'));
    } else {
      setCardRegistrars(allRegistrars);
    }
  };

  const handleFilter = filter => {
    const value = filter.target.value;
    let newFilterState = filters;

    // User clicked on All filter
    if (value === '') {
      newFilterState = [];
    } else {
      if (newFilterState.includes(value)) {
        const index = newFilterState.indexOf(value);

        if (index > -1) {
          newFilterState.splice(index, 1);
        }
      } else {
        newFilterState = [...filters, value];
      }
    }

    setFilters(newFilterState);
  };

  return (
    <div className="wrapper">
      <div className="step__inner">
        <div className="step__content">
          <p className="headline">Step 3</p>
          <div className="step__title">
            <h3>{isJumpstarter ? 'Pick a registrar to buy a domain from' : 'Pick your registrar'}</h3>
            <MoreInfo content={helperStepThree} isJumpstarter={isJumpstarter} ariaLabel='Open pick your registrar info panel'/>
          </div>
          {!isJumpstarter &&
            <p className="h6 step__description">
              Below you’ll find a list of authorised registrars where
              you can purchase your domain. Choose the right one for
              your needs and you’ll be taken to their website to finish
              the process.
            </p>
          }
          {isJumpstarter &&
            <p className="h6 step__description">
              Below is a list of authorised registrars where you can purchase your .nz domain.
              Choose any of these that meet your needs, and you’ll be taken to their website to finish
              the registration and purchase process, which will take approximately two minutes.
            </p>
          }
        </div>
        <div className="mt-40">
          {(loading || error) && (
            <ErrorMessage
              message={loading ? LOADING_MSG : ERROR_MSG}
              error={error}
            />
          )}
        </div>

        <div className="step__cardholder">
          {cardRegistrars.length > 0 &&
            cardRegistrars.map(({ Name, ImageLink, CheckoutUrl, Tier, TierLabel }, index) => {
                // Swap the query character with the selected domain
                const checkout =
                  CheckoutUrl &&
                  CheckoutUrl.replace(QUERY_SELECTOR, selectedDomain);

                const delay = Math.max(0, index * 175);

                return (
                  <CSSTransition
                    in={true}
                    key={Name}
                    appear
                    timeout={!mediaMobile() ? 1000 + delay : 0}
                    classNames="fade"
                  >
                    <Card
                      key={`card${Name}`}
                      title={Name}
                      imageUrl={ImageLink}
                      checkoutUrl={checkout}
                      styles={{
                        transitionDelay: `${
                          !mediaMobile() ? delay : 0
                        }ms`,
                      }}
                      noStatus
                      isRegistrar
                      tierLabel={groupInTiers && Tier === 'Gold' ? TierLabel : ''}
                      isJumpstarter={isJumpstarter}
                      selectedDomain={selectedDomain}
                    />
                  </CSSTransition>
                );
              },
            )}
          {!showAllRegistrars &&
            <div className="step__view-more-button col-16">
              <button
                className="button c-btn c-btn--xlarge"
                onClick={showAll}
                aria-label="Show more registrars"
              >
                {groupInTiers ? 'See more registrars' : 'View more'}
              </button>
            </div>
          }
          {showAllRegistrars && listRegistrars.length > 0 &&
            <ul className="step__registrar-list">
              {listRegistrars.map(({ Name, CheckoutUrl }, index) => {
                // Swap the query character with the selected domain
                const checkout =
                  CheckoutUrl &&
                  CheckoutUrl.replace(QUERY_SELECTOR, selectedDomain);

                const delay = Math.max(0, index * 90);

                return (
                  <CSSTransition
                    in={true}
                    key={Name}
                    appear
                    timeout={!mediaMobile() ? 1000 + delay : 0}
                    classNames="fade"
                  >
                    <RegistrarRow
                      key={`registrar-${Name}`}
                      title={Name}
                      checkoutUrl={checkout}
                      styles={{
                        transitionDelay: `${
                          !mediaMobile() ? delay : 0
                        }ms`,
                      }}
                      selectedDomain={selectedDomain}
                    />
                  </CSSTransition>
                );
              })}
            </ul>
          }
          {!isJumpstarter && (
            <div className="step__registrar-link col-16">
              <a className="link--with-chevron" href="/registrars">
                View all registrars
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Step3.propTypes = {
  selectedDomain: PropTypes.string,
  helperStepThree: PropTypes.string,
  registrarFilters: PropTypes.array,
  groupInTiers: PropTypes.bool,
  isJumpstarter: PropTypes.bool,
};

export default Step3;
